import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useState, useRef } from "react";
import styled from "styled-components";

import { ClaimDetailsModal } from "../../../components/ClaimDetailsModal/ClaimDetailsModal";
import { formatRootCauses } from "../../../components/ClaimDetailsModal/EstimateComparisonTab";
import { parseDate } from "../../../helpers/date";
import {
  useGetClaimsQuery,
  useLazyExportClaimsQuery,
  useGetDistinctClearPayersQuery,
  useGetClaimByIdQuery,
} from "../../../store/queries/claims";
import { IClaim, IVarianceAnalysis } from "../../../types/claim";
import {
  extractClaimPatientResponsibility,
  formatCurrency,
  formatDollars,
} from "../../../utils/claimAdjustments";

const CompactDataTable = styled(DataTable)`
  .p-datatable-tbody > tr > td {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
  }

  .p-datatable-thead > tr > th {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    line-height: 1.3 !important;
  }

  .p-column-title {
    white-space: normal !important;
    word-wrap: break-word !important;
  }
`;

const PatientNameLink = styled.span`
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0056b3;
  }
`;

const OrderLink = styled.a`
  color: #007bff;
  text-decoration: none;
  font-size: 0.85rem;

  &:hover {
    text-decoration: underline;
    color: #0056b3;
  }
`;

function getVarianceColor(positive?: boolean, negative?: boolean): string {
  if (positive) return "#28a745";
  if (negative) return "#dc3545";
  return "inherit";
}

const VarianceCell = styled.span<{ $positive?: boolean; $negative?: boolean }>`
  font-weight: ${({ $positive, $negative }) =>
    $positive || $negative ? 600 : 400};
  color: ${({ $positive, $negative }) =>
    getVarianceColor($positive, $negative)};
`;

const RootCauseList = styled.div`
  font-size: 0.8rem;
  line-height: 1.3;
`;

const RootCauseItem = styled.div`
  margin-bottom: 0.15rem;
`;

const RootCauseLabel = styled.span`
  font-weight: 600;
  color: #495057;
`;

const RootCauseDetail = styled.span`
  color: #6c757d;
  margin-left: 0.25rem;
`;

interface IEstimateInsightsListProps {
  accountId: string;
}

// Helper to get FACILITY procedures only from pricing breakdown
function getFacilityProcedures(pricingBreakdown: any): any[] {
  if (!pricingBreakdown?.procedureBreakdown?.length) return [];
  return pricingBreakdown.procedureBreakdown.filter(
    (proc: any) => proc.specialty === "FACILITY"
  );
}

// Build estimate case signature from FACILITY procedures
function getEstimateCaseSignature(pricingBreakdown: any): string {
  const procs = getFacilityProcedures(pricingBreakdown);
  if (procs.length === 0) return "-";
  return procs
    .map((p: any) => `${p.code}:${p.quantity ?? 1}`)
    .sort()
    .join(",");
}

// Get estimate allowed amount (sum of FACILITY amountInCents)
function getEstimateAllowed(pricingBreakdown: any): number | null {
  const procs = getFacilityProcedures(pricingBreakdown);
  if (procs.length === 0) return null;
  return procs.reduce((sum: number, p: any) => sum + (p.amountInCents ?? 0), 0);
}

// Get estimate patient OOP (sum of FACILITY owedByPatient.total)
function getEstimateOOP(pricingBreakdown: any): number | null {
  const procs = getFacilityProcedures(pricingBreakdown);
  if (procs.length === 0) return null;
  return procs.reduce(
    (sum: number, p: any) => sum + (p.owedByPatient?.total ?? 0),
    0
  );
}

// Get estimate deductible (sum of FACILITY owedByPatient.deductible)
function getEstimateDeductible(pricingBreakdown: any): number | null {
  const procs = getFacilityProcedures(pricingBreakdown);
  if (procs.length === 0) return null;
  return procs.reduce(
    (sum: number, p: any) => sum + (p.owedByPatient?.deductible ?? 0),
    0
  );
}

// Get estimate coinsurance (sum of FACILITY owedByPatient.coinsurance)
function getEstimateCoinsurance(pricingBreakdown: any): number | null {
  const procs = getFacilityProcedures(pricingBreakdown);
  if (procs.length === 0) return null;
  return procs.reduce(
    (sum: number, p: any) => sum + (p.owedByPatient?.coinsurance ?? 0),
    0
  );
}

// Format a variance between two cent amounts
function formatVariance(
  estimateCents: number | null,
  actualCents: number | null
): { text: string; positive: boolean; negative: boolean } {
  if (estimateCents === null || actualCents === null) {
    return { text: "-", positive: false, negative: false };
  }
  const diff = actualCents - estimateCents;
  const text = formatCurrency(diff);
  return {
    text: diff > 0 ? `+${text.replace("-", "")}` : text,
    positive: diff > 0,
    negative: diff < 0,
  };
}

// Format variance for dollar amounts (835 data already in dollars)
function formatDollarVariance(
  estimateCents: number | null,
  actualDollars: number
): { text: string; positive: boolean; negative: boolean } {
  if (estimateCents === null) {
    return { text: "-", positive: false, negative: false };
  }
  const estimateDollars = estimateCents / 100;
  const diff = actualDollars - estimateDollars;
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(diff));
  const text = diff >= 0 ? `+${formatted}` : `-${formatted}`;
  return {
    text: diff === 0 ? "$0.00" : text,
    positive: diff > 0,
    negative: diff < 0,
  };
}

function EstimateInsightsList({ accountId }: IEstimateInsightsListProps) {
  const toast = useRef<Toast>(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [clearPayerFilter, setClearPayerFilter] = useState<string | undefined>(
    undefined
  );

  // Pagination and sorting
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState("serviceDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Modal state
  const [selectedRow, setSelectedRow] = useState<IClaim | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [claimHistory, setClaimHistory] = useState<IClaim[]>([]);
  const [navigateToClaimId, setNavigateToClaimId] = useState<string | null>(
    null
  );

  // Query payer dropdown options
  const { data: clearPayers = [] } = useGetDistinctClearPayersQuery({
    accountId,
  });

  // Fetch claims with matched orders only
  const {
    data: claimsResponse,
    isLoading,
    isFetching,
  } = useGetClaimsQuery({
    accountId,
    search: search || undefined,
    startDate: dateRange[0] || undefined,
    endDate: dateRange[1] || undefined,
    sortField,
    sortOrder,
    page,
    pageSize,
    hasMatchedOrder: true,
    clearPayerFilter,
    paymentTypes: ["all"],
    resolutionFilter: "all",
  });

  // Export query
  const [triggerExport, { isFetching: isExporting }] =
    useLazyExportClaimsQuery();

  // Navigate to claim by ID (for modal navigation)
  const { data: navigatedClaim, isFetching: isNavigatedClaimFetching } =
    useGetClaimByIdQuery(
      { claimId: navigateToClaimId! },
      { skip: !navigateToClaimId }
    );

  // Update modal when navigated claim loads
  React.useEffect(() => {
    if (navigatedClaim && !isNavigatedClaimFetching) {
      setSelectedRow(navigatedClaim);
      setNavigateToClaimId(null);
    }
  }, [navigatedClaim, isNavigatedClaimFetching]);

  const claims = claimsResponse?.data || [];
  const totalRecords = claimsResponse?.totalCount || 0;

  // Handlers
  const handleSort = (e: any) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const handlePage = (e: any) => {
    setPage(Math.floor(e.first / e.rows) + 1);
    setPageSize(e.rows);
  };

  const handleRowClick = (claim: IClaim) => {
    setSelectedRow(claim);
    setModalVisible(true);
  };

  const handleNavigateToClaim = (claimId: string) => {
    if (selectedRow) {
      setClaimHistory((prev) => [...prev, selectedRow]);
    }
    setNavigateToClaimId(claimId);
  };

  const handleBackToClaim = () => {
    const prev = claimHistory[claimHistory.length - 1];
    if (prev) {
      setSelectedRow(prev);
      setClaimHistory((h) => h.slice(0, -1));
    }
  };

  const handleCloseModal = () => {
    setClaimHistory([]);
    setNavigateToClaimId(null);
  };

  const handleExport = async () => {
    try {
      const result = await triggerExport({
        accountId,
        search: search || undefined,
        startDate: dateRange[0] || undefined,
        endDate: dateRange[1] || undefined,
        sortField,
        sortOrder,
        hasMatchedOrder: true,
        clearPayerFilter,
        paymentTypes: ["all"],
        resolutionFilter: "all",
      }).unwrap();

      if (result.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
      }
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Export Failed",
        detail: "Failed to generate export file",
        life: 5000,
      });
    }
  };

  // Payer dropdown options
  const payerOptions = [
    { label: "All Payers", value: undefined },
    ...clearPayers.map((p: string) => ({ label: p, value: p })),
  ];

  // ===== Column Templates =====

  const patientNameTemplate = (rowData: IClaim) => (
    <PatientNameLink onClick={() => handleRowClick(rowData)}>
      {rowData.patientName}
    </PatientNameLink>
  );

  const serviceDateTemplate = (rowData: IClaim) =>
    parseDate(rowData.serviceDate);

  const estimateSignatureTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const pricingBreakdown = rowData.order.gfeQuote.pricingBreakdown as any;
    const sig = getEstimateCaseSignature(pricingBreakdown);
    // Format CPT codes 4 per line for readability
    const codes = sig.split(",");
    const lines: string[] = [];
    for (let i = 0; i < codes.length; i += 4) {
      lines.push(codes.slice(i, i + 4).join(", "));
    }
    return (
      <span style={{ fontSize: "0.8rem", fontFamily: "monospace" }}>
        {lines.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const actualSignatureTemplate = (rowData: IClaim) => {
    const codes = (rowData.caseSignature || "").split(",");
    const lines: string[] = [];
    for (let i = 0; i < codes.length; i += 4) {
      lines.push(codes.slice(i, i + 4).join(", "));
    }
    return (
      <span style={{ fontSize: "0.8rem", fontFamily: "monospace" }}>
        {lines.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const signatureVarianceTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const pricingBreakdown = rowData.order.gfeQuote.pricingBreakdown as any;
    const estSig = getEstimateCaseSignature(pricingBreakdown);
    const actSig = rowData.caseSignature || "";

    if (estSig === "-") return "-";
    if (estSig === actSig) {
      return <Tag severity="success" value="Match" />;
    }
    return <Tag severity="warning" value="Code Change" />;
  };

  const estimateAllowedTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const amount = getEstimateAllowed(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    return formatCurrency(amount);
  };

  const actualAllowedTemplate = (rowData: IClaim) =>
    formatCurrency(rowData.claimAmount);

  const allowedVarianceTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const estAllowed = getEstimateAllowed(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    const v = formatVariance(estAllowed, rowData.claimAmount);
    return (
      <VarianceCell $positive={v.positive} $negative={v.negative}>
        {v.text}
      </VarianceCell>
    );
  };

  const estimateDeductibleTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const amount = getEstimateDeductible(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    return formatCurrency(amount);
  };

  const actualDeductibleTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.deductible > 0 ? formatDollars(resp.deductible) : "-";
  };

  const deductibleVarianceTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const estCents = getEstimateDeductible(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    const v = formatDollarVariance(estCents, resp.deductible);
    return (
      <VarianceCell $positive={v.positive} $negative={v.negative}>
        {v.text}
      </VarianceCell>
    );
  };

  const estimateCoinsuranceTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const amount = getEstimateCoinsurance(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    return formatCurrency(amount);
  };

  const actualCoinsuranceTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.coinsurance > 0 ? formatDollars(resp.coinsurance) : "-";
  };

  const coinsuranceVarianceTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const estCents = getEstimateCoinsurance(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    const v = formatDollarVariance(estCents, resp.coinsurance);
    return (
      <VarianceCell $positive={v.positive} $negative={v.negative}>
        {v.text}
      </VarianceCell>
    );
  };

  const estimateOOPTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const amount = getEstimateOOP(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    return formatCurrency(amount);
  };

  const actualOOPTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.totalOOP > 0 ? formatDollars(resp.totalOOP) : "-";
  };

  const oopVarianceTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const estCents = getEstimateOOP(
      rowData.order.gfeQuote.pricingBreakdown as any
    );
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    const v = formatDollarVariance(estCents, resp.totalOOP);
    return (
      <VarianceCell $positive={v.positive} $negative={v.negative}>
        {v.text}
      </VarianceCell>
    );
  };

  const rootCauseTemplate = (rowData: IClaim) => {
    const causes = formatRootCauses(
      rowData.varianceAnalysis as IVarianceAnalysis | null | undefined,
      rowData.lineItems
    );
    if (causes.length === 0) {
      return <span style={{ color: "#6c757d" }}>-</span>;
    }
    return (
      <RootCauseList>
        {causes.map((cause, idx) => (
          <RootCauseItem key={idx}>
            <RootCauseLabel>{cause.label}:</RootCauseLabel>
            <RootCauseDetail>{cause.detail}</RootCauseDetail>
          </RootCauseItem>
        ))}
      </RootCauseList>
    );
  };

  const orderLinkTemplate = (rowData: IClaim) => {
    if (!rowData.orderId) return "-";
    return (
      <OrderLink
        href={`/orders/${rowData.orderId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        View Order
      </OrderLink>
    );
  };

  return (
    <>
      <Toast ref={toast} />

      {/* Filters */}
      <div
        className="flex flex-wrap align-items-center gap-2 mb-3"
        style={{ padding: "0.5rem 0" }}
      >
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search patient, payer, claim #..."
            style={{ width: "280px" }}
          />
        </span>

        <Calendar
          value={dateRange as CalendarValueType}
          onChange={(e) => {
            setDateRange((e.value as Date[]) || []);
            setPage(1);
          }}
          selectionMode="range"
          placeholder="Service Date Range"
          showIcon
          showButtonBar
          style={{ width: "260px" }}
        />

        <Dropdown
          value={clearPayerFilter}
          options={payerOptions}
          onChange={(e) => {
            setClearPayerFilter(e.value);
            setPage(1);
          }}
          placeholder="All Payers"
          style={{ width: "220px" }}
          showClear={!!clearPayerFilter}
        />

        <div className="flex-grow-1" />

        <Button
          label="Export"
          icon="pi pi-download"
          className="p-button-outlined p-button-sm"
          onClick={handleExport}
          loading={isExporting}
        />
      </div>

      {/* Data Table */}
      <CompactDataTable
        value={claims}
        lazy
        paginator
        rows={pageSize}
        totalRecords={totalRecords}
        first={(page - 1) * pageSize}
        onPage={handlePage}
        onSort={handleSort}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
        rowsPerPageOptions={[10, 25, 50]}
        loading={isLoading || isFetching}
        emptyMessage="No claims with matched estimates found."
        scrollable
        scrollHeight="calc(100vh - 320px)"
        size="small"
      >
        <Column
          field="patientName"
          header="Patient Name"
          sortable
          body={patientNameTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="serviceDate"
          header="Service Date"
          sortable
          body={serviceDateTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="claimPayerName"
          header="Payer Name"
          sortable
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="clearPayerName"
          header="Matched Payer"
          sortable
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Est. Signature"
          body={estimateSignatureTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="caseSignature"
          header="Actual Signature"
          body={actualSignatureTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Sig. Variance"
          body={signatureVarianceTemplate}
          style={{ minWidth: "7rem", textAlign: "center" }}
        />
        <Column
          header="Est. Allowed"
          sortable
          sortField="estimateAllowed"
          body={estimateAllowedTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="claimAmount"
          header="Actual Allowed"
          sortable
          body={actualAllowedTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Allowed Variance"
          sortable
          sortField="allowedVariance"
          body={allowedVarianceTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Est. Deductible"
          sortable
          sortField="estimateDeductible"
          body={estimateDeductibleTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Actual Deductible"
          body={actualDeductibleTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Ded. Variance"
          sortable
          sortField="deductibleVariance"
          body={deductibleVarianceTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Est. Coinsurance"
          sortable
          sortField="estimateCoinsurance"
          body={estimateCoinsuranceTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Actual Coinsurance"
          body={actualCoinsuranceTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Coins. Variance"
          sortable
          sortField="coinsuranceVariance"
          body={coinsuranceVarianceTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Est. Pt OOP"
          sortable
          sortField="estimateOOP"
          body={estimateOOPTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Actual Pt OOP"
          body={actualOOPTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="OOP Variance"
          sortable
          sortField="oopVariance"
          body={oopVarianceTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Detected Root Cause"
          body={rootCauseTemplate}
          style={{ minWidth: "14rem" }}
        />
        <Column
          header="Order"
          body={orderLinkTemplate}
          style={{ minWidth: "6rem", textAlign: "center" }}
        />
      </CompactDataTable>

      {/* Claim Details Modal */}
      {selectedRow && (
        <ClaimDetailsModal
          visible={modalVisible}
          onHide={() => {
            setModalVisible(false);
            handleCloseModal();
          }}
          accountId={accountId}
          claim={selectedRow}
          onNavigateToClaim={handleNavigateToClaim}
          onBackToClaim={
            claimHistory.length > 0 ? handleBackToClaim : undefined
          }
          isNavigating={isNavigatedClaimFetching}
          onClaimUpdated={(updatedClaim) => {
            setSelectedRow(updatedClaim);
          }}
        />
      )}
    </>
  );
}

export default EstimateInsightsList;
